import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	useController,
	useForm,
} from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import {
	useGetFieldOptionCommonDetailQuery,
	useGetFieldOptionCommonListQuery,
	useGetFieldOptionRegionDetailQuery,
	useGetFieldOptionRegionListQuery,
	useUpdateFieldOptionCommonDetailOrderMutation,
	useUpdateFieldOptionRegionDetailOrderMutation,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import DndArticleListSection from './components/re-order/DndArticleListSection';
import { arrayMove } from '@dnd-kit/sortable';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
	title?: string;
	type?: number;
}
export default function FieldCommonOrderPage(props: Props) {
	const { title, type } = props;
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { fieldId, fieldType, agencyType, referralType, agencyId, referralId } =
		useParams();
	const [updateFieldOptionCommonDetailOrder] =
		useUpdateFieldOptionCommonDetailOrderMutation();

	const { data: dictDetails } = useGetFieldOptionCommonDetailQuery(
		fieldType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 1 && !fieldType,
		}
	);
	const { data: agencyDetails } = useGetFieldOptionCommonDetailQuery(
		agencyType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 2 && !agencyType,
		}
	);
	const { data: referralDetails } = useGetFieldOptionCommonDetailQuery(
		referralType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 3 && !referralType,
		}
	);

	const [meditationItems, setMeditationItems] = useState<any>([]);

	const defaultValues: DefaultValues<any> = {};
	const methods = useForm<any>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionCommonListQuery(
			{
				pagination: { page: 1, size: 99999 },
				searchString: '',
				sortings: [],
				type: type === 2 ? agencyType : type === 3 ? referralType : fieldType,
				parentId: type === 2 ? agencyId : type === 3 ? referralId : '',
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	useEffect(() => {
		if (propertySummary) {
			if (propertySummary?.data?.items) {
				setMeditationItems(propertySummary?.data?.items || []);
			}
		}
	}, [propertySummary]);

	const handleRepositionItem = (activeIndex: number, overIndex: number) => {
		setMeditationItems((prev: unknown[]) =>
			arrayMove(prev, activeIndex, overIndex)
		);
	};

	const handleSubmit = async () => {
		const response = await updateFieldOptionCommonDetailOrder(
			(meditationItems || []).map((item: any) => item.id)
		);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFieledUnitOrderSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				type === 2
					? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}`
					: type === 3
					? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/referral/${referralId}/${referralType}`
					: `/field-management/field-management-list/field/${fieldId}/${fieldType}`
			);
		}
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{type === 2
						? `${t('field.manageStatus')} (${agencyDetails?.data.labelDisplay})`
						: type === 3
						? `${t('field.manageStatus')} (${
								referralDetails?.data.labelDisplay
						  })`
						: `${t('field.manageStatus')} (${dictDetails?.data.labelDisplay})`}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<DndArticleListSection
								meditationItems={transformResponse(meditationItems) ?? []}
								handleRepositionItem={handleRepositionItem}
								dictDetails={dictDetails}
								agencyDetails={agencyDetails}
								referralDetails={referralDetails}
								type={type}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					onClick={() => {
						navigate(
							type === 2
								? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}`
								: type === 3
								? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/referral/${referralId}/${referralType}`
								: `/field-management/field-management-list/field/${fieldId}/${fieldType}`
						);
					}}
				>
					{t('button.cancel')}
				</Button>
				<Button type='submit' variant='contained' onClick={handleSubmit}>
					{t('button.complete')}
				</Button>
			</Box>
		</>
	);
}
